// Central Deal State Schema for AcquireOS MVP
// This is the most critical artifact - single source of truth for all deal data

export const INITIAL_DEAL_STATE = {
  // Core deal identification
  dealId: null,
  dealName: null,
  createdAt: null,
  updatedAt: null,
  initialRawDealData: null,
  
  // Current pipeline stage
  pipelineStage: 1, // 1-16 based on module completion
  pipelineStatus: "not_started", // not_started, in_progress, completed, blocked
  
  // Module 1: Deal Lead Generation
  rawDealData: {
    source: null, // "broker_email", "listings", "manual_upload"
    price: null,
    location: {
      address: null,
      city: null,
      state: null,
      zip: null,
      latitude: null,
      longitude: null
    },
    property: {
      units: null,
      buildingType: null, // "multifamily", "commercial", "mixed_use"
      yearBuilt: null,
      squareFeet: null,
      acres: null
    },
    initialAIScore: null, // 0-100
    dealLeadGenerationDetails: {
      brokerName: null,
      listingUrl: null,
      contactInfo: null,
      urgency: null, // "high", "medium", "low"
      competitionLevel: null // "high", "medium", "low"
    }
  },
  
  // Module 2: Market Intelligence / Data Extraction
  extractedFinancials: {
    revenue: {
      grossPotentialRent: null,
      otherIncome: null,
      totalRevenue: null,
      rentRoll: {
        totalUnits: null,
        occupiedUnits: null,
        occupancyRate: null,
        averageRent: null,
        unitMix: [] // array of unit types and counts
      }
    },
    expenses: {
      operatingExpenses: {
        propertyTaxes: null,
        insurance: null,
        repairs: null,
        maintenance: null,
        utilities: null,
        propertyManagement: null,
        marketing: null,
        administrative: null,
        other: null
      },
      totalOperatingExpenses: null,
      capitalExpenditures: null,
      totalExpenses: null
    },
    noi: null, // Net Operating Income
    dataValidation: {
      missingFields: [],
      alerts: [],
      dataQuality: null, // "high", "medium", "low"
      completeness: null // 0-100 percentage
    },
    marketIntelligenceDetails: {
      marketRent: null,
      marketOccupancy: null,
      comparableProperties: [],
      marketTrends: null
    }
  },
  
  // Module 3: Deal Intake - Financial Modeling View
  financialModeling: {
    cashFlow: {
      monthlyCashFlow: null,
      annualCashFlow: null,
      cashOnCashReturn: null
    },
    incomeVsExpenses: {
      revenueBreakdown: {},
      expenseBreakdown: {},
      profitMargin: null
    },
    scenarios: {
      baseCase: {
        noi: null,
        cashFlow: null,
        capRate: null,
        irr: null
      },
      bestCase: {
        noi: null,
        cashFlow: null,
        capRate: null,
        irr: null
      },
      worstCase: {
        noi: null,
        cashFlow: null,
        capRate: null,
        irr: null
      }
    },
    assumptions: {
      vacancyRate: null,
      rentGrowthRate: null,
      expenseGrowthRate: null,
      capExRate: null
    }
  },
  
  // ── Ownership Cost Summary ────────────────────────────────────────────────
  // Single source of truth for all cost components so every module (3–16) reads
  // the same numbers instead of computing them independently.
  ownershipCosts: {
    // Insurance
    monthlyInsurance: null,         // $/mo
    annualInsurance: null,          // $/yr
    insuranceIsEstimated: false,    // true when estimated from purchase price (not OM/T12)

    // Property operating expenses (utilities + maintenance + management + reserves)
    monthlyPropertyOpEx: null,      // $/mo — excludes insurance and taxes
    annualPropertyOpEx: null,       // $/yr

    // Property taxes (may be null when not extracted from documents)
    monthlyPropertyTax: null,       // $/mo
    annualPropertyTax: null,        // $/yr

    // Debt service
    monthlyDebtService: null,       // $/mo — principal + interest
    annualDebtService: null,        // $/yr

    // Grand totals
    totalMonthlyOwnershipCost: null, // all monthly costs combined
    totalAnnualOwnershipCost: null,  // all annual costs combined
  },

  // Module 4: Underwriting Engine - Underwriting Metrics
  underwritingMetrics: {
    noi: null,
    capRate: {
      entry: null,
      exit: null,
      market: null
    },
    dscr: {
      current: null,
      minimum: null,
      average: null
    },
    irr: {
      unlevered: null,
      levered: null,
      holdingPeriod: null
    },
    cashOnCash: {
      year1: null,
          average: null,
      peak: null
    },
    roi: null,
    breakEvenOccupancy: null,
    debtService: {
      monthly: null,
      annual: null,
      interestRate: null,
      loanAmount: null,
      ltv: null
    },
    // Itemised expense snapshot used by NOI, DSCR, cash flow, break-even, IRR, and score.
    // Populated by the Underwriting Engine (Module 4) and read by Modules 5–16.
    operatingExpenseDetail: {
      annualInsurance: null,
      monthlyInsurance: null,
      insuranceIsEstimated: false,
      annualPropertyTax: null,
      monthlyPropertyTax: null,
      annualPropertyOpEx: null,    // utilities + maintenance + management + reserves
      monthlyPropertyOpEx: null,
      totalAnnualExpenses: null,
      totalMonthlyExpenses: null,
    },
    scenarioAnalysis: {
      sensitivity: {},
      stressTest: {}
    },
    underwritingEngineDetails: {
      modelVersion: null,
      assumptionsUsed: [],
      riskFactors: [],
      confidenceLevel: null
    }
  },
  
  // Module 5: Deal Score & Verdict
  scoreAndOffer: {
    numericalScore: null, // 0-100
    verdict: null, // "STRONG_BUY", "BUY", "REVIEW", "PASS", "STRONG_PASS"
    scoreBreakdown: {
      financialStrength: null,
      marketPosition: null,
      riskLevel: null,
      valueAdd: null
    },
    strengths: [],
    risks: [],
    offerPrice: {
      recommended: null,
      minimum: null,
      maximum: null,
      confidence: null
    },
    dealScoreVerdictDetails: {
      scoringModel: null,
      keyDrivers: [],
      redFlags: [],
      blueFlags: []
    }
  },
  
  // Module 6: Lender Match Engine
  lenderMatches: {
    matchedLenders: [],
    topRecommendation: null,
    matchingCriteria: {
      loanAmount: null,
      ltv: null,
      dscr: null,
      propertyType: null,
      location: null
    },
    lenderAnalysis: {
      totalMatches: null,
      averageRate: null,
      bestRate: null,
      approvalLikelihood: null
    }
  },
  
  // Module 7: Loan Package Generator
  loanPackage: {
    documentId: null,
    executiveSummary: null,
    propertyOverview: null,
    financialHighlights: null,
    rentRollSummary: null,
    underwritingSummary: null,
    generatedAt: null,
    format: null // "pdf", "docx"
  },
  
  // Module 8: Outreach & Communication
  outreachMaterials: {
    emailTemplates: [],
    callScript: null,
    smsTemplate: null,
    personalization: {
      lenderName: null,
      dealSpecificVars: {}
    }
  },
  
  // Module 9: Actions & Follow-up
  actionPlan: {
    tasks: [],
    priorities: [],
    timeline: null,
    statusTracking: {}
  },
  
  // Module 10: LOI Generator
  loiDocument: {
    documentId: null,
    offerPrice: null,
    timeline: null,
    earnestMoney: null,
    contingencies: [],
    generatedAt: null
  },
  
  // Module 11: PSA Assistant
  psaDocument: {
    documentId: null,
    buyerDetails: null,
    sellerDetails: null,
    terms: null,
    clauses: [],
    editableSections: []
  },
  
  // Module 12: Escrow & Title Workflow
  escrowDetails: {
    escrowCompany: null,
    escrowNumber: null,
    earnestMoneyDeposited: null,
    titleSearch: {
      chainOfTitle: null,
      ownershipVerified: null,
      publicRecordChecks: null
    },
    titleIssues: {
      liens: [],
      taxes: [],
      legalEncumbrances: []
    },
    titleCommitment: null,
    milestones: {
      escrowOpened: null,
      titleCleared: null,
      closingReady: null
    },
    progressPercentage: null
  },
  
  // Module 13: Due Diligence & Title Clearance
  dueDiligence: {
    financialValidation: null,
    propertyInspection: null,
    titleIssueResolution: {
      curativeWork: [],
      legalFixes: []
    },
    riskScoring: {
      overallRisk: null,
      issueSeverity: []
    },
    finalClearanceConfirmed: null
  },
  
  // Module 14: Financing, Closing & Recording
  closingDetails: {
    loanApproval: null,
    closingDisclosure: null,
    stakeholderCoordination: {
      buyer: null,
      seller: null,
      lender: null,
      titleCompany: null
    },
    fundsVerification: {
      downPayment: null,
      loanFunds: null,
      escrowBalance: null
    },
    documentSigning: null,
    fundDisbursement: {
      sellerPayout: null,
      feesDistribution: null
    },
    recording: {
      deedRegistered: null,
      ownershipTransferred: null,
      titleInsuranceIssued: null
    }
  },
  
  // Module 15: Asset Plan & Hold
  assetManagement: {
    kpiDashboard: {
      occupancy: null,
      rent: null,
      noi: null,
      performanceVsTarget: null
    },
    managementPlan: null,
    performanceTracking: null
  },
  
  // Module 16: Exit Strategy
  exitStrategy: {
    analysis: {
      sell: {
        roi: null,
        timing: null,
        projectedValue: null
      },
      refinance: {
        roi: null,
        timing: null,
        cashOut: null
      }
    },
    recommendation: null,
    exitTimeline: null
  }
};

// Helper function to validate deal state completeness
export const validateDealState = (dealState, moduleNumber) => {
  const validation = {
    isValid: true,
    missingFields: [],
    errors: []
  };
  
  // Module-specific validation rules
  switch (moduleNumber) {
    case 1:
      if (!dealState.rawDealData.source) {
        validation.isValid = false;
        validation.missingFields.push("rawDealData.source");
      }
      if (!dealState.rawDealData.price && dealState.rawDealData.source !== 'loopnet_live') {
        validation.isValid = false;
        validation.missingFields.push("rawDealData.price");
      }
      break;
      
    case 2:
      if (!dealState.extractedFinancials.revenue.totalRevenue) {
        validation.isValid = false;
        validation.missingFields.push("extractedFinancials.revenue.totalRevenue");
      }
      if (!dealState.extractedFinancials.noi) {
        validation.isValid = false;
        validation.missingFields.push("extractedFinancials.noi");
      }
      break;
      
    case 3:
      // Module 3 (Deal Intake) is complete when OM is uploaded and financial data has been extracted
      if (!dealState.financialDocuments?.omUploaded) {
        validation.isValid = false;
        validation.missingFields.push("financialDocuments.omUploaded");
      }
      if (!dealState.extractedFinancials.revenue.totalRevenue) {
        validation.isValid = false;
        validation.missingFields.push("extractedFinancials.revenue.totalRevenue");
      }
      if (!dealState.extractedFinancials.noi) {
        validation.isValid = false;
        validation.missingFields.push("extractedFinancials.noi");
      }
      break;
      
    case 4:
      if (!dealState.underwritingMetrics?.capRate?.entry) {
        validation.isValid = false;
        validation.missingFields.push("underwritingMetrics.capRate.entry");
      }
      if (!dealState.underwritingMetrics?.dscr?.current) {
        validation.isValid = false;
        validation.missingFields.push("underwritingMetrics.dscr.current");
      }
      break;
      
    case 5:
      if (!dealState.scoreAndOffer?.numericalScore) {
        validation.isValid = false;
        validation.missingFields.push("scoreAndOffer.numericalScore");
      }
      if (!dealState.scoreAndOffer?.verdict) {
        validation.isValid = false;
        validation.missingFields.push("scoreAndOffer.verdict");
      }
      break;

    case 6:
      if (!dealState.lenderMatches?.matchedLenders?.length) {
        validation.isValid = false;
        validation.missingFields.push("lenderMatches.matchedLenders");
      }
      if (!dealState.lenderMatches?.topRecommendation) {
        validation.isValid = false;
        validation.missingFields.push("lenderMatches.topRecommendation");
      }
      break;

    case 7:
      if (!dealState.syndicateDecisionMade) {
        validation.isValid = false;
        validation.missingFields.push("syndicateDecisionMade");
      }
      if (
        dealState.syndicateWanted &&
        !dealState.syndicateSkipped &&
        !dealState.syndicateData?.completedAt
      ) {
        validation.isValid = false;
        validation.missingFields.push("syndicateData.completedAt");
      }
      break;

    case 8:
      if (!dealState.loanPackage?.documentId) {
        validation.isValid = false;
        validation.missingFields.push("loanPackage.documentId");
      }
      if (!dealState.loanPackage?.executiveSummary) {
        validation.isValid = false;
        validation.missingFields.push("loanPackage.executiveSummary");
      }
      break;

    case 9:
      if (!dealState.outreachMaterials?.emailTemplates?.length) {
        validation.isValid = false;
        validation.missingFields.push("outreachMaterials.emailTemplates");
      }
      break;

    case 10:
      if (!dealState.actionPlan?.tasks?.length) {
        validation.isValid = false;
        validation.missingFields.push("actionPlan.tasks");
      }
      if (!dealState.actionPlan?.timeline) {
        validation.isValid = false;
        validation.missingFields.push("actionPlan.timeline");
      }
      break;

    case 11:
      if (!dealState.loiDocument?.documentId) {
        validation.isValid = false;
        validation.missingFields.push("loiDocument.documentId");
      }
      if (!dealState.loiDocument?.offerPrice) {
        validation.isValid = false;
        validation.missingFields.push("loiDocument.offerPrice");
      }
      break;

    case 12:
      if (!dealState.psaDocument?.documentId) {
        validation.isValid = false;
        validation.missingFields.push("psaDocument.documentId");
      }
      if (!dealState.psaDocument?.terms) {
        validation.isValid = false;
        validation.missingFields.push("psaDocument.terms");
      }
      break;

    case 13:
      if (!dealState.escrowDetails?.escrowNumber) {
        validation.isValid = false;
        validation.missingFields.push('escrowDetails.escrowNumber');
      }
      break;

    case 14:
      if (!dealState.dueDiligence?.finalClearanceConfirmed) {
        validation.isValid = false;
        validation.missingFields.push('dueDiligence.finalClearanceConfirmed');
      }
      break;

    case 15:
      if (!dealState.closingDetails?.recording?.deedRegistered) {
        validation.isValid = false;
        validation.missingFields.push('closingDetails.recording.deedRegistered');
      }
      break;

    case 16:
      if (!dealState.assetManagement?.kpiDashboard?.activatedAt) {
        validation.isValid = false;
        validation.missingFields.push('assetManagement.kpiDashboard.activatedAt');
      }
      break;

    case 17:
      if (!dealState.exitStrategy?.completedAt) {
        validation.isValid = false;
        validation.missingFields.push('exitStrategy.completedAt');
      }
      if (!dealState.exitStrategy?.recommendation) {
        validation.isValid = false;
        validation.missingFields.push('exitStrategy.recommendation');
      }
      break;

    default:
      validation.isValid = false;
      validation.missingFields.push(`module${moduleNumber}.completionCriteria`);
      break;
  }
  
  return validation;
};
